<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { getReadableInboxByType } from 'dashboard/helper/inbox';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PriorityCell from './tableCells/PriorityCell.vue';
import AssigneeCell from './tableCells/AssigneeCell.vue';
import StatusCell from './tableCells/StatusCell.vue';
import {
  PRIORITY_RANK,
  STATE_RANK,
  TICKET_TYPE_META,
} from 'dashboard/helper/conversationLens';

const props = defineProps({
  tickets: { type: Array, default: () => [] },
  // 'tickets' is the tickets screen, 'conversations' the inbox grid. Both show
  // the same columns; the variant only decides the copy and the secondary line
  // under the subject.
  variant: {
    type: String,
    default: 'tickets',
    validator: value => ['tickets', 'conversations'].includes(value),
  },
});

const emit = defineEmits(['open', 'markSolved']);

const { t } = useI18n();

const HEAD_CELL_CLASS =
  'sticky top-0 z-10 whitespace-nowrap bg-n-solid-2 px-4 py-2.5 text-start';

// The table is fixed-layout so it always fits its container - every column
// carries its own width and every cell truncates rather than scrolling the
// table sideways.
const COLUMNS = [
  {
    key: 'customer',
    labelKey: 'TICKETS.TABLE.COLUMNS.CUSTOMER',
    width: 'w-[22%]',
  },
  {
    key: 'subject',
    labelKey: 'TICKETS.TABLE.COLUMNS.SUBJECT',
    width: 'w-auto',
  },
  {
    key: 'priority',
    labelKey: 'TICKETS.TABLE.COLUMNS.PRIORITY',
    width: 'w-[124px]',
  },
  {
    key: 'assignee',
    labelKey: 'TICKETS.TABLE.COLUMNS.TEAMMATE',
    width: 'w-[140px]',
  },
  {
    key: 'status',
    labelKey: 'TICKETS.TABLE.COLUMNS.STATUS',
    width: 'w-[124px]',
  },
  {
    key: 'lastMessage',
    labelKey: 'TICKETS.TABLE.COLUMNS.LAST_MESSAGE',
    // Wide enough for the column's own header, which is longer than any of
    // the relative timestamps under it.
    width: 'w-[124px]',
    alignEnd: true,
  },
];

const isConversationsVariant = computed(
  () => props.variant === 'conversations'
);

// The variant's own copy for the selection bar and footer, so the inbox grid
// never talks about "tickets".
const copy = computed(() =>
  isConversationsVariant.value ? 'CONVERSATION.TABLE' : 'TICKETS.TABLE'
);

const channelLabel = ticket => {
  if (!ticket.source) return '';
  const slug = getReadableInboxByType(
    ticket.source,
    ticket.customer?.phoneNumber
  );
  return t(`CONVERSATION.TABLE.CHANNELS.${slug}`);
};

// The line under the subject: the ticket number on the tickets screen, the
// channel the conversation arrived on in the inbox grid.
const subjectMeta = ticket => {
  const lead = isConversationsVariant.value
    ? channelLabel(ticket)
    : `#${ticket.id}`;
  return [lead, ticket.preview].filter(Boolean).join(' \u00b7 ');
};

// Unassigned rows and unprioritised rows sort to the bottom of an ascending
// sort rather than to the top, which is where an agent expects to find them.
const SORT_VALUES = {
  customer: ticket => (ticket.customer.name || '').toLowerCase(),
  subject: ticket => (ticket.subject || '').toLowerCase(),
  priority: ticket => PRIORITY_RANK[ticket.priority] ?? 0,
  assignee: ticket => (ticket.assignee?.name || '\uffff').toLowerCase(),
  status: ticket => STATE_RANK[ticket.state],
  lastMessage: ticket => ticket.lastActivityAt ?? 0,
};

const checkedIds = ref([]);
const sort = ref({ key: 'lastMessage', direction: 'desc' });

const sortedTickets = computed(() => {
  const read = SORT_VALUES[sort.value.key];
  const factor = sort.value.direction === 'asc' ? 1 : -1;
  return [...props.tickets].sort((left, right) => {
    const a = read(left);
    const b = read(right);
    if (a === b) return 0;
    return a > b ? factor : -factor;
  });
});

const allChecked = computed(
  () =>
    props.tickets.length > 0 && checkedIds.value.length === props.tickets.length
);

const toggleAll = () => {
  checkedIds.value = allChecked.value
    ? []
    : props.tickets.map(ticket => ticket.id);
};

const toggleOne = id => {
  checkedIds.value = checkedIds.value.includes(id)
    ? checkedIds.value.filter(checkedId => checkedId !== id)
    : [...checkedIds.value, id];
};

const toggleSort = key => {
  const { key: currentKey, direction } = sort.value;
  if (currentKey === key) {
    sort.value = { key, direction: direction === 'asc' ? 'desc' : 'asc' };
    return;
  }
  sort.value = { key, direction: key === 'lastMessage' ? 'desc' : 'asc' };
};

const sortIcon = key => {
  if (sort.value.key !== key) return 'i-lucide-arrow-up-down';
  return sort.value.direction === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};

const ariaSort = key => {
  if (sort.value.key !== key) return 'none';
  return sort.value.direction === 'asc' ? 'ascending' : 'descending';
};

const timeAgo = timestamp =>
  timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';

const markSolved = () => {
  emit('markSolved', [...checkedIds.value]);
  checkedIds.value = [];
};

// A view change swaps the rows underneath the selection, so it is dropped.
watch(
  () => props.tickets,
  () => {
    checkedIds.value = checkedIds.value.filter(id =>
      props.tickets.some(ticket => ticket.id === id)
    );
  }
);
</script>

<template>
  <div
    class="overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2"
  >
    <div
      v-if="checkedIds.length"
      class="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-n-weak bg-n-violet-3"
    >
      <span class="text-sm font-medium text-n-violet-11">
        {{ t(`${copy}.SELECTED`, { count: checkedIds.length }) }}
      </span>
      <button
        type="button"
        class="text-sm font-medium text-n-violet-11 hover:underline"
        @click="markSolved"
      >
        {{ t(`${copy}.MARK_SOLVED`) }}
      </button>
      <button
        type="button"
        :title="t(`${copy}.CLEAR_SELECTION`)"
        :aria-label="t(`${copy}.CLEAR_SELECTION`)"
        class="grid ms-auto size-6 place-items-center rounded-md text-n-violet-11 hover:bg-n-alpha-2"
        @click="checkedIds = []"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <div>
      <table class="w-full border-collapse table-fixed">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              scope="col"
              class="sticky top-0 z-10 w-10 bg-n-solid-2 px-4 py-2.5 align-middle"
            >
              <button
                type="button"
                role="checkbox"
                :aria-checked="allChecked"
                :aria-label="t(`${copy}.SELECT_ALL`)"
                class="flex items-center justify-center !border !border-solid rounded size-4 mx-auto"
                :class="
                  allChecked
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-n-slate-10 bg-n-solid-1 text-transparent hover:border-n-slate-12'
                "
                @click="toggleAll"
              >
                <span class="i-lucide-check size-3 block shrink-0" />
              </button>
            </th>
            <th
              v-for="column in COLUMNS"
              :key="column.key"
              scope="col"
              :aria-sort="ariaSort(column.key)"
              :class="[
                HEAD_CELL_CLASS,
                column.width,
                column.alignEnd ? 'text-end' : '',
              ]"
            >
              <button
                type="button"
                :data-testid="`sort-${column.key}`"
                class="inline-flex items-center gap-1.5 text-xs font-medium max-w-full"
                :class="
                  sort.key === column.key
                    ? 'text-n-slate-12'
                    : 'text-n-slate-10'
                "
                @click="toggleSort(column.key)"
              >
                {{ t(column.labelKey) }}
                <span :class="sortIcon(column.key)" class="size-3.5" />
              </button>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="ticket in sortedTickets"
            :key="ticket.id"
            data-testid="ticket-row"
            class="cursor-pointer group/row hover:bg-n-alpha-1"
            :class="checkedIds.includes(ticket.id) ? 'bg-n-alpha-1' : ''"
            @click="emit('open', ticket.id)"
          >
            <td class="px-4 py-3 align-middle" @click.stop>
              <button
                type="button"
                role="checkbox"
                :aria-checked="checkedIds.includes(ticket.id)"
                :aria-label="
                  t(`${copy}.SELECT_ONE`, { subject: ticket.subject })
                "
                class="flex items-center justify-center !border !border-solid rounded size-4 mx-auto"
                :class="
                  checkedIds.includes(ticket.id)
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-n-slate-10 bg-n-solid-1 text-transparent hover:border-n-slate-12'
                "
                @click="toggleOne(ticket.id)"
              >
                <span class="i-lucide-check size-3 block shrink-0" />
              </button>
            </td>
            <template v-for="column in COLUMNS" :key="column.key">
              <td v-if="column.key === 'customer'" class="px-4 py-3">
                <div class="flex items-center gap-2.5">
                  <Avatar
                    :name="ticket.customer.name"
                    :src="ticket.customer.thumbnail"
                    :size="32"
                    rounded-full
                  />
                  <div class="min-w-0">
                    <p class="m-0 text-sm font-medium truncate text-n-slate-12">
                      {{ ticket.customer.name }}
                    </p>
                    <p class="m-0 text-xs truncate text-n-slate-10">
                      {{ ticket.customer.email }}
                    </p>
                  </div>
                </div>
              </td>
              <td v-else-if="column.key === 'subject'" class="px-4 py-3">
                <div class="flex items-center gap-1.5">
                  <span
                    :class="TICKET_TYPE_META[ticket.type].icon"
                    class="size-3.5 shrink-0 text-n-slate-10"
                    :title="t(TICKET_TYPE_META[ticket.type].labelKey)"
                  />
                  <p
                    class="m-0 text-sm truncate"
                    :class="
                      ticket.unread
                        ? 'font-medium text-n-slate-12'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ ticket.subject || t('TICKETS.EMPTY.NO_SUBJECT') }}
                  </p>
                </div>
                <p class="m-0 text-xs truncate text-n-slate-10">
                  {{ subjectMeta(ticket) }}
                </p>
              </td>
              <td
                v-else-if="column.key === 'priority'"
                class="px-4 py-3"
                @click.stop
              >
                <PriorityCell
                  :conversation-id="ticket.id"
                  :priority="ticket.priority"
                />
              </td>
              <td
                v-else-if="column.key === 'assignee'"
                class="px-4 py-3"
                @click.stop
              >
                <AssigneeCell
                  :conversation-id="ticket.id"
                  :inbox-id="ticket.conversation.inbox_id"
                  :assignee="ticket.assignee"
                />
              </td>
              <td
                v-else-if="column.key === 'status'"
                class="px-4 py-3"
                @click.stop
              >
                <StatusCell
                  :conversation-id="ticket.id"
                  :state="ticket.state"
                />
              </td>
              <td
                v-else-if="column.key === 'lastMessage'"
                class="px-4 py-3 text-sm truncate text-end text-n-slate-11"
              >
                {{ timeAgo(ticket.lastActivityAt) }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex items-center px-4 py-3 border-t border-n-weak">
      <p data-testid="ticket-count" class="m-0 text-xs text-n-slate-10">
        {{ t(`${copy}.SHOWING`, { count: sortedTickets.length }) }}
      </p>
    </div>
  </div>
</template>
